
import { useState, useEffect } from "react";
import "./AboutUs.css";
import {
  FaBullseye,
  FaEye,
  FaEnvelope,
  FaArrowRight,
  FaStar,
  FaGamepad,
  FaFilm,
  FaHeart,
  FaUsers,
  FaCheckCircle,
} from "react-icons/fa";

const aboutImages = [
  "/images/about-main.jpg",
  "/images/fe1.jpg",
  "/images/fe2.jpg",
];

const missionPoints = [
  "Creating private, comfortable spaces for meaningful celebrations.",
  "Personalizing special occasions to make every moment unique.",
  "Bringing entertainment, food, and celebration options together.",
];

const AboutUs = () => {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrent((prev) => (prev + 1) % aboutImages.length);
    }, 4000);

    return () => clearInterval(timer);
  }, []);

  return (
    <main className="about-page">
      {/* Hero Header */}
      <section className="about-hero">
        <div className="about-hero-glow about-hero-glow-one" />
        <div className="about-hero-glow about-hero-glow-two" />

        <div className="about-hero-content">
          <span className="about-eyebrow">
            <FaStar />
            DISCOVER THE HEXAPLAY EXPERIENCE
          </span>

          <h1>
            Where Every Moment
            <br />
            Becomes <span>Memorable.</span>
          </h1>

          <p>
            A next-generation entertainment destination where private
            celebrations, immersive gaming, and extraordinary experiences
            come together under one roof.
          </p>

          <div className="about-hero-highlights">
            <span>
              <FaFilm /> Private Theatre
            </span>
            <span>
              <FaGamepad /> Immersive Gaming
            </span>
            <span>
              <FaHeart /> Personal Celebrations
            </span>
          </div>
        </div>
      </section>

      {/* About Story */}
      <section className="about-story-section">
        <div className="about-container">
          <div className="about-content">
            <span className="about-section-label">OUR STORY</span>

            <h2>
              More Than a Place.
              <br />
              <span>It's an Experience.</span>
            </h2>

            <p className="about-story-text">
              At HexaPlay, we believe the best moments are the ones you get
              to experience your own way. We bring private theatre
              entertainment, gaming, and personalized celebrations together
              in a destination designed around you.
            </p>

            <p className="about-story-text">
              Whether you are planning a birthday, anniversary, date night,
              proposal, kids' celebration, or a fun day with friends, we
              help turn ordinary outings into extraordinary memories.
              From the atmosphere to the smallest personal touches, every
              experience is an opportunity to celebrate differently.
            </p>

            <p className="about-story-text">
              Our philosophy is simple: your occasion should feel like
              yours. With privacy, comfort, and a variety of entertainment
              options, HexaPlay gives you the freedom to create moments
              worth remembering.
            </p>

            <a href="/Services" className="about-cta-btn">
              Book Your Experience
              <FaArrowRight />
            </a>
          </div>

          {/* Image Slider */}
          <div className="about-image">
            <div className="about-slider">
              {aboutImages.map((img, index) => (
                <img
                  key={img}
                  src={img}
                  alt={[
                    "The HexaPlay entertainment experience",
                    "HexaPlay private celebration space",
                    "HexaPlay gaming and entertainment",
                  ][index]}
                  className={`about-slide ${
                    index === current ? "active" : ""
                  }`}
                  loading={index === 0 ? "eager" : "lazy"}
                />
              ))}

              <div className="about-image-overlay" />

              <div className="about-image-caption">
                <span>YOUR MOMENT. YOUR EXPERIENCE.</span>
                <h3>Make It Unforgettable.</h3>
              </div>

              <div
                className="about-dots"
                aria-label="Choose an experience image"
              >
                {aboutImages.map((img, index) => (
                  <button
                    key={img}
                    type="button"
                    className={`dot ${
                      index === current ? "active" : ""
                    }`}
                    onClick={() => setCurrent(index)}
                    aria-label={`Show image ${index + 1}`}
                    aria-pressed={index === current}
                  />
                ))}
              </div>
            </div>

            <div className="about-image-decoration">
              <FaStar />
              <div>
                <strong>Made for Memories</strong>
                <span>Experiences that bring people together</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vision and Mission */}
      <section className="vision-mission-section">
        <div className="vm-heading">
          <span className="about-section-label">WHAT DRIVES US</span>

          <h2>
            Our Vision &amp; <span>Mission</span>
          </h2>

          <p>
            The purpose behind every experience we create and every
            memory we help make.
          </p>
        </div>

        <div className="vision-mission">
          {/* Vision Card */}
          <article className="vm-card vm-vision-card">
            <div className="vm-card-top">
              <div className="vm-icon-wrap">
                <FaEye className="vm-icon" />
              </div>

              <span className="vm-number">01</span>
            </div>

            <span className="vm-kicker">OUR FUTURE</span>

            <h3>Our Vision</h3>

            <p className="vm-description">
              To become a destination where people come together to create
              meaningful moments, not simply spend time. We envision
              HexaPlay as a place where celebrations feel personal,
              entertainment feels exciting, and every visit creates
              memories worth cherishing.
            </p>

            <p className="vm-description">
              As we expand into new locations and introduce new experiences,
              we remain committed to preserving the privacy, personalization,
              comfort, and quality that make every HexaPlay visit special.
            </p>

            <div className="vm-card-footer">
              <span>Our aspiration</span>
              <span className="vm-footer-icon">
                <FaArrowRight />
              </span>
            </div>
          </article>

          {/* Mission Card */}
          <article className="vm-card vm-mission-card">
            <div className="vm-card-top">
              <div className="vm-icon-wrap">
                <FaBullseye className="vm-icon" />
              </div>

              <span className="vm-number">02</span>
            </div>

            <span className="vm-kicker">OUR PURPOSE</span>

            <h3>Our Mission</h3>

            <p className="vm-description">
              To bring celebration, entertainment, and personalization
              together through thoughtfully designed experiences that
              make spending time together more enjoyable, convenient,
              and memorable.
            </p>

            <ul className="vm-mission-list">
              {missionPoints.map((point, index) => (
                <li key={index}>
                  <FaCheckCircle className="vm-check-icon" />
                  <span>{point}</span>
                </li>
              ))}
            </ul>

            <div className="vm-card-footer">
              <span>Our commitment</span>
              <span className="vm-footer-icon">
                <FaArrowRight />
              </span>
            </div>
          </article>
        </div>
      </section>

      {/* Company and Leadership */}
      <section className="company-section">
        <div className="company-inner">
          <div className="company-icon-wrap">
            <FaUsers />
          </div>

          <span className="about-section-label">
            THE PEOPLE BEHIND THE EXPERIENCE
          </span>

          <h2>
            Company &amp; <span>Leadership</span>
          </h2>

          <p className="company-desc">
            The HexaPlay is proudly owned and operated by{" "}
            <strong>NP Groups</strong>, a Pondicherry-based business group
            focused on innovation, premium experiences, and forward-thinking
            entertainment concepts. Driven by a vision to build distinctive
            and immersive destinations, NP Groups is committed to developing
            high-quality experiential spaces that redefine how people
            celebrate, play, connect, and create unforgettable memories.
          </p>

          <div className="company-values">
            <div className="company-value">
              <span className="company-value-icon">
                <FaStar />
              </span>
              <div>
                <h4>Quality Experiences</h4>
                <p>Thoughtfully designed entertainment.</p>
              </div>
            </div>

            <div className="company-value">
              <span className="company-value-icon">
                <FaHeart />
              </span>
              <div>
                <h4>People First</h4>
                <p>Every celebration deserves attention.</p>
              </div>
            </div>

            <div className="company-value">
              <span className="company-value-icon">
                <FaGamepad />
              </span>
              <div>
                <h4>Constant Innovation</h4>
                <p>Fresh ways to enjoy every visit.</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default AboutUs;
